import { type FC, useState } from "react";

import { CourseCodeSelectionList } from "./CourseCodeSelectionList";
import { MoveCourseCodesConfirmationModal } from "./MoveCourseCodesConfirmationModal";

import { serverAddCourseCodesToGrouping } from "api/api-server-actions";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { CourseCodeWithGroupingType } from "store/selectors/types";
import { toastSuccessMessage } from "utils/alerts";

type MoveCourseCodesToGroupingModalProps = {
  groupingId: string;
  courseCodes: CourseCodeWithGroupingType[];
  onClose: () => void;
};

export const MoveCourseCodesToGroupingModal: FC<MoveCourseCodesToGroupingModalProps> = ({
  groupingId,
  courseCodes,
  onClose,
}) => {
  const [selectedCourseCodeIds, setSelectedCourseCodeIds] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const filteredCourseCodes = courseCodes.filter(
    (courseCode) => courseCode.grouping_id !== groupingId,
  );

  const toggleCourseCodeChecked = (courseCodeId: string) => {
    const newSelectedIds = [...selectedCourseCodeIds];

    const codeIndex = newSelectedIds.indexOf(courseCodeId);
    if (codeIndex > -1) {
      newSelectedIds.splice(codeIndex, 1);
    } else {
      newSelectedIds.push(courseCodeId);
    }

    setSelectedCourseCodeIds(newSelectedIds);
  };

  const handleSubmit = () => {
    if (selectedCourseCodeIds.length === 0) {
      return;
    }

    // Check if any selected course codes belong to a different grouping
    const courseCodesFromOtherGroupings = filteredCourseCodes.filter(
      (code) =>
        selectedCourseCodeIds.includes(code.id) &&
        code.grouping_id !== null &&
        code.grouping_id !== groupingId,
    );

    if (courseCodesFromOtherGroupings.length > 0) {
      // Show confirmation modal
      setShowConfirmation(true);
      return;
    }

    // No course codes from other groupings, proceed directly
    handleMoveCourseCodes();
  };

  const handleMoveCourseCodes = async () => {
    await serverAddCourseCodesToGrouping(groupingId, selectedCourseCodeIds);
    toastSuccessMessage(
      t("home.toasts.course_code_moved", { count: selectedCourseCodeIds.length }),
    );
    onClose();
  };

  const handleConfirmationCancel = () => {
    setShowConfirmation(false);
  };

  const handleConfirmationConfirm = async () => {
    await handleMoveCourseCodes();
  };

  if (showConfirmation) {
    const courseCodesFromOtherGroupings = filteredCourseCodes.filter(
      (code) =>
        selectedCourseCodeIds.includes(code.id) &&
        code.grouping_id !== null &&
        code.grouping_id !== groupingId,
    );

    return (
      <MoveCourseCodesConfirmationModal
        courseCodes={courseCodesFromOtherGroupings}
        onCancel={handleConfirmationCancel}
        onConfirm={handleConfirmationConfirm}
      />
    );
  }

  return (
    <div className="mt-8 flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <CourseCodeSelectionList
          courseCodes={filteredCourseCodes}
          selectedCourseCodeIds={selectedCourseCodeIds}
          onCourseCodeToggle={toggleCourseCodeChecked}
        />
      </div>

      <div className="flex w-full justify-end gap-2">
        <Button kind="white" size="s" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </Button>

        <Button
          disabled={selectedCourseCodeIds.length === 0}
          kind="blue"
          size="s"
          title={t("home.course_codes.move_selected_course_codes")}
          onClick={handleSubmit}
        >
          {t("home.course_codes.move_selected_course_codes")}
        </Button>
      </div>
    </div>
  );
};
